import { create } from 'zustand';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { getManifest } from '@/extensions/manifests';
import { isExtensionEnabled, resolveSettings, type UserExtensionRow } from '@/extensions/settings';
import type { ExtensionSettingsValues } from '@/extensions/types';

interface ExtensionsState {
  rows: Record<string, UserExtensionRow>;
  loaded: boolean;
  load: () => Promise<void>;
  isEnabled: (extensionId: string) => boolean;
  getSettings: (extensionId: string) => ExtensionSettingsValues;
  setEnabled: (extensionId: string, enabled: boolean) => Promise<void>;
  saveSettings: (extensionId: string, settings: ExtensionSettingsValues) => Promise<boolean>;
}

async function currentUserId(): Promise<string | null> {
  const { data } = await supabase.auth.getUser();
  return data.user?.id ?? null;
}

let loading: Promise<void> | null = null;

export const useExtensionsStore = create<ExtensionsState>((set, get) => ({
  rows: {},
  loaded: false,

  load: () => {
    // Several components ask on mount; they share one request
    if (loading) return loading;
    loading = (async () => {
      const { data, error } = await supabase
        .from('user_extensions')
        .select('extension_id, enabled, settings');
      if (error) {
        // Table missing (migration not applied yet) or offline: fall back to
        // manifest defaults rather than breaking the toolbar.
        console.warn('Failed to load extensions:', error);
        set({ loaded: true });
        return;
      }
      const rows: Record<string, UserExtensionRow> = {};
      for (const row of data ?? []) {
        rows[row.extension_id] = row as UserExtensionRow;
      }
      set({ rows, loaded: true });
    })().finally(() => {
      loading = null;
    });
    return loading;
  },

  isEnabled: (extensionId) => {
    const manifest = getManifest(extensionId);
    return manifest ? isExtensionEnabled(manifest, get().rows[extensionId]) : false;
  },

  getSettings: (extensionId) => {
    const manifest = getManifest(extensionId);
    return manifest ? resolveSettings(manifest, get().rows[extensionId]?.settings) : {};
  },

  setEnabled: async (extensionId, enabled) => {
    const userId = await currentUserId();
    if (!userId) return;
    const previous = get().rows[extensionId];
    const optimistic: UserExtensionRow = {
      extension_id: extensionId,
      enabled,
      settings: previous?.settings ?? {},
    };
    set((s) => ({ rows: { ...s.rows, [extensionId]: optimistic } }));

    const { error } = await supabase
      .from('user_extensions')
      .upsert(
        { user_id: userId, extension_id: extensionId, enabled, updated_at: new Date().toISOString() },
        { onConflict: 'user_id,extension_id' },
      );
    if (error) {
      console.error('Failed to update extension:', error);
      toast.error('Could not update the extension. Please try again.');
      set((s) => {
        const rows = { ...s.rows };
        if (previous) rows[extensionId] = previous;
        else delete rows[extensionId];
        return { rows };
      });
    }
  },

  saveSettings: async (extensionId, settings) => {
    const userId = await currentUserId();
    if (!userId || !getManifest(extensionId)) return false;
    const enabled = get().isEnabled(extensionId);

    const { error } = await supabase
      .from('user_extensions')
      .upsert(
        { user_id: userId, extension_id: extensionId, enabled, settings, updated_at: new Date().toISOString() },
        { onConflict: 'user_id,extension_id' },
      );
    if (error) {
      console.error('Failed to save extension settings:', error);
      toast.error('Could not save settings. Please try again.');
      return false;
    }
    set((s) => ({ rows: { ...s.rows, [extensionId]: { extension_id: extensionId, enabled, settings } } }));
    return true;
  },
}));
